import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Boxes, Tag, BarChart3, RotateCcw,
  FileText, Ticket, Wrench, CalendarDays, LineChart, Settings, ChevronDown, Sparkles,
} from "lucide-react";
import { useState } from "react";

const groups = [
  {
    label: "Overview",
    items: [
      { to: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
      { to: "/admin/analytics", icon: LineChart, label: "Analytics" },
    ],
  },
  {
    label: "Commerce",
    items: [
      { to: "/admin/products", icon: Package, label: "Products" },
      { to: "/admin/orders", icon: ShoppingCart, label: "Orders" },
      { to: "/admin/customers", icon: Users, label: "Customers" },
      { to: "/admin/inventory", icon: Boxes, label: "Inventory" },
      { to: "/admin/promotions", icon: Tag, label: "Promotions" },
      { to: "/admin/after-sales", icon: RotateCcw, label: "Returns / Refunds" },
      { to: "/admin/finance", icon: BarChart3, label: "Finance" },
      { to: "/admin/cms", icon: FileText, label: "CMS" },
    ],
  },
  {
    label: "Service Operations",
    items: [
      { to: "/admin/tickets", icon: Ticket, label: "Tickets" },
      { to: "/admin/technicians", icon: Wrench, label: "Technicians" },
      { to: "/admin/schedule", icon: CalendarDays, label: "Schedule" },
    ],
  },
];

export function AdminSidebar({ collapsed }: { collapsed: boolean }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isActive = (to: string, exact?: boolean) => (exact ? path === to : path === to || path.startsWith(to + "/"));
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(groups.map((g) => [g.label, true]))
  );

  return (
    <aside
      className={`sticky top-0 h-screen shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-all duration-300 ${
        collapsed ? "w-16" : "w-64"
      }`}
    >
      <div className="flex h-16 items-center gap-2 px-4 border-b border-sidebar-border">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#d7d5c7] shadow-glow">
          <img src="/assets/images/kizak_logo_icon_v1.png" alt="KIZAK" className="h-6 w-6" />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">KIZAK Admin</span>
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin Console</span>
          </div>
        )}
      </div>

      <nav className="flex flex-col gap-4 overflow-y-auto px-2 py-4 scrollbar-thin" style={{ height: "calc(100vh - 4rem)" }}>
        {groups.map((g) => (
          <div key={g.label}>
            {!collapsed && (
              <button
                onClick={() => setOpenGroups((s) => ({ ...s, [g.label]: !s[g.label] }))}
                className="flex w-full items-center justify-between px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
              >
                <span>{g.label}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${openGroups[g.label] ? "" : "-rotate-90"}`} />
              </button>
            )}
            {(collapsed || openGroups[g.label]) && (
              <ul className="space-y-0.5">
                {g.items.map((it) => {
                  const active = isActive(it.to, it.exact);
                  return (
                    <li key={it.to}>
                      <Link
                        to={it.to}
                        className={`group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all ${
                          active
                            ? "bg-sidebar-accent text-sidebar-primary font-medium shadow-sm"
                            : "text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
                        }`}
                      >
                        <it.icon className={`h-4 w-4 shrink-0 ${active ? "text-sidebar-primary" : ""}`} />
                        {!collapsed && <span className="truncate">{it.label}</span>}
                        {active && !collapsed && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-sidebar-primary" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}

        {!collapsed && (
          <div className="mt-auto rounded-xl border border-sidebar-border bg-sidebar-accent/40 p-3">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium">System status</span>
              <span className="ml-auto h-2 w-2 rounded-full bg-success pulse-dot" />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">All services operational</p>
          </div>
        )}
      </nav>
    </aside>
  );
}